/* Potter Painter Hiring — form validation, file upload and submission. Vanilla JS, no dependencies. */
(function () {
  'use strict';

  var ALLOWED_EXT = ['pdf', 'jpg', 'jpeg', 'png'];
  var ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var HANDLE_RE = /^@[A-Za-z0-9._]{1,30}$/;
  var STORAGE_PREFIX = 'pph-draft-';

  var MSG = {
    portfolio_required: 'Please add a link to your portfolio, website or Instagram.',
    portfolio_invalid: 'Enter a full link (e.g. yoursite.com) or an Instagram handle starting with @.',
    full_name: 'Please enter your full name.',
    location: 'Please choose where in LA you are based.',
    email_required: 'Please enter your email address.',
    email_invalid: 'Please enter a valid email address, like you@email.com.',
    phone_required: 'Please enter your phone number.',
    phone_invalid: 'Please enter a valid phone number, including area code.',
    file_type: 'That file type isn’t supported. Please upload a PDF, JPG or PNG.',
    file_size: function (mb) { return 'That file is too large. The maximum size is ' + mb + 'MB.'; },
    summary: 'Please fix the highlighted fields and try again.',
    network: 'We couldn’t send your application. Please check your connection and try again.',
    server: 'Something went wrong on our side and your application wasn’t received. Please try again in a moment.',
    sending: 'Sending your application…',
    sent: 'Application received.'
  };

  function isValidUrl(value) {
    if (/\s/.test(value)) return false;
    var candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : 'https://' + value;
    try {
      var url = new URL(candidate);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
      return /^[^.]+(\.[^.]+)+$/.test(url.hostname) && !/\.$/.test(url.hostname);
    } catch (e) {
      return false;
    }
  }

  function phoneDigits(value) {
    return (value.match(/\d/g) || []).length;
  }

  function PphForm(root) {
    this.root = root;
    this.form = root.querySelector('form.pph__form');
    if (!this.form) return; // success state rendered by Shopify

    this.mode = root.getAttribute('data-mode');
    this.endpoint = root.getAttribute('data-endpoint') || '';
    this.sheetUrl = root.getAttribute('data-sheet-url') || '';
    this.notifyEmail = root.getAttribute('data-notify-email') || '';
    this.maxBytes = parseInt(root.getAttribute('data-max-bytes'), 10) || 20971520;
    this.maxMb = root.getAttribute('data-max-mb') || '20';
    this.loadingLabel = root.getAttribute('data-loading-label') || MSG.sending;
    this.submitBtn = this.form.querySelector('[data-pph-submit]');
    this.submitLabel = this.form.querySelector('[data-pph-submit-label]');
    this.defaultLabel = this.submitLabel ? this.submitLabel.textContent : '';
    this.formError = this.form.querySelector('[data-pph-form-error]');
    this.live = this.form.querySelector('[data-pph-live]');
    this.success = root.querySelector('[data-pph-success]');
    this.fileInput = this.form.querySelector('input[type="file"]');
    this.upload = this.form.querySelector('[data-pph-upload]');
    this.fileName = this.form.querySelector('[data-pph-file-name]');
    this.fileRemove = this.form.querySelector('[data-pph-file-remove]');
    this.defaultFileTitle = this.fileName ? this.fileName.textContent : '';
    this.select = this.form.querySelector('select');
    this.submitting = false;
    this.storageKey = STORAGE_PREFIX + (this.form.id || '');

    // JS takes over validation; without JS the browser's native validation still applies.
    this.form.setAttribute('novalidate', '');
    this.bind();
    this.restoreDraft();
    this.syncSelect();
  }

  PphForm.prototype.field = function (key) {
    return this.form.querySelector('[data-key="' + key + '"]');
  };

  PphForm.prototype.bind = function () {
    var self = this;
    this.form.addEventListener('submit', function (e) { self.onSubmit(e); });

    // Re-validate a field once it has been flagged, as the user corrects it.
    this.form.addEventListener('input', function (e) {
      var key = e.target.getAttribute('data-key');
      if (key && e.target.getAttribute('aria-invalid') === 'true') self.validateField(key);
    });
    this.form.addEventListener('change', function (e) {
      if (e.target === self.select) {
        self.syncSelect();
        if (self.select.getAttribute('aria-invalid') === 'true') self.validateField('location');
      }
    });
    this.form.addEventListener('focusout', function (e) {
      var key = e.target.getAttribute && e.target.getAttribute('data-key');
      if (key && key !== 'portfolio_file' && e.target.value) self.validateField(key);
    });

    if (this.fileInput) {
      this.fileInput.addEventListener('change', function () { self.onFileChange(); });
      if (this.fileRemove) {
        this.fileRemove.addEventListener('click', function () {
          self.clearFile();
          self.fileInput.focus();
        });
      }
      var zone = this.upload;
      ['dragenter', 'dragover'].forEach(function (type) {
        zone.addEventListener(type, function (e) {
          e.preventDefault();
          zone.classList.add('is-dragover');
        });
      });
      ['dragleave', 'dragend', 'drop'].forEach(function (type) {
        zone.addEventListener(type, function () { zone.classList.remove('is-dragover'); });
      });
      zone.addEventListener('drop', function (e) {
        e.preventDefault();
        var files = e.dataTransfer && e.dataTransfer.files;
        if (!files || !files.length) return;
        try {
          var dt = new DataTransfer();
          dt.items.add(files[0]);
          self.fileInput.files = dt.files;
        } catch (err) {
          return; // very old browsers: fall back to click-to-browse
        }
        self.onFileChange();
      });
    }

    // Restore the button if the page is shown again from the back/forward cache.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) self.setLoading(false);
    });
  };

  PphForm.prototype.syncSelect = function () {
    if (this.select) this.select.classList.toggle('is-empty', !this.select.value);
  };

  /* ---------- Validation ---------- */
  PphForm.prototype.setError = function (key, message) {
    var el = this.field(key);
    var err = this.form.querySelector('[data-error-for="' + key + '"]');
    if (el) {
      if (message) el.setAttribute('aria-invalid', 'true');
      else el.removeAttribute('aria-invalid');
    }
    if (key === 'portfolio_file' && this.upload) this.upload.classList.toggle('is-invalid', !!message);
    if (err) {
      err.textContent = message || '';
      err.hidden = !message;
    }
    return !message;
  };

  PphForm.prototype.validateField = function (key) {
    var el = this.field(key);
    if (!el) return true;
    var value = (el.value || '').trim();

    switch (key) {
      case 'portfolio':
        if (!value) return this.setError(key, MSG.portfolio_required);
        if (value.charAt(0) === '@') return this.setError(key, HANDLE_RE.test(value) ? '' : MSG.portfolio_invalid);
        return this.setError(key, isValidUrl(value) ? '' : MSG.portfolio_invalid);
      case 'full_name':
        return this.setError(key, value.length >= 2 ? '' : MSG.full_name);
      case 'location':
        return this.setError(key, value ? '' : MSG.location);
      case 'email':
        if (!value) return this.setError(key, MSG.email_required);
        return this.setError(key, EMAIL_RE.test(value) ? '' : MSG.email_invalid);
      case 'phone':
        if (!value) return this.setError(key, MSG.phone_required);
        var digits = phoneDigits(value);
        var ok = /^[0-9+().\-\s]+$/.test(value) && digits >= 10 && digits <= 15;
        return this.setError(key, ok ? '' : MSG.phone_invalid);
      case 'portfolio_file':
        return this.setError(key, this.checkFile());
      default:
        return true;
    }
  };

  PphForm.prototype.checkFile = function () {
    if (!this.fileInput || !this.fileInput.files || !this.fileInput.files.length) return '';
    var file = this.fileInput.files[0];
    var ext = (file.name.split('.').pop() || '').toLowerCase();
    var typeOk = ALLOWED_EXT.indexOf(ext) !== -1 && (!file.type || ALLOWED_MIME.indexOf(file.type) !== -1);
    if (!typeOk) return MSG.file_type;
    if (file.size > this.maxBytes) return MSG.file_size(this.maxMb);
    return '';
  };

  PphForm.prototype.validateAll = function () {
    var keys = ['portfolio', 'portfolio_file', 'full_name', 'location', 'email', 'phone'];
    var firstInvalid = null;
    for (var i = 0; i < keys.length; i++) {
      if (!this.validateField(keys[i]) && !firstInvalid) firstInvalid = this.field(keys[i]);
    }
    return firstInvalid;
  };

  /* ---------- File handling ---------- */
  PphForm.prototype.onFileChange = function () {
    var file = this.fileInput.files && this.fileInput.files[0];
    var error = this.checkFile();
    this.setError('portfolio_file', error);
    if (!file || error) {
      if (error) this.fileInput.value = '';
      this.showFile(null);
      return;
    }
    this.showFile(file);
  };

  PphForm.prototype.showFile = function (file) {
    if (!this.fileName) return;
    this.fileName.textContent = file ? file.name : this.defaultFileTitle;
    if (this.fileRemove) this.fileRemove.hidden = !file;
    if (this.upload) this.upload.classList.toggle('has-file', !!file);
    if (file) this.announce('Selected file: ' + file.name);
  };

  PphForm.prototype.clearFile = function () {
    if (!this.fileInput) return;
    this.fileInput.value = '';
    this.setError('portfolio_file', '');
    this.showFile(null);
    this.announce('File removed.');
  };

  /* ---------- Status helpers ---------- */
  PphForm.prototype.announce = function (text) {
    if (!this.live) return;
    this.live.textContent = '';
    var live = this.live;
    window.setTimeout(function () { live.textContent = text; }, 50);
  };

  PphForm.prototype.showFormError = function (text) {
    if (!this.formError) return;
    this.formError.textContent = text || '';
    this.formError.hidden = !text;
  };

  PphForm.prototype.setLoading = function (on) {
    this.submitting = on;
    if (!this.submitBtn) return;
    this.submitBtn.disabled = on;
    if (on) this.submitBtn.setAttribute('aria-busy', 'true');
    else this.submitBtn.removeAttribute('aria-busy');
    if (this.submitLabel) this.submitLabel.textContent = on ? this.loadingLabel : this.defaultLabel;
  };

  /* ---------- Draft (keeps custom field values if Shopify returns a recoverable error) ---------- */
  PphForm.prototype.saveDraft = function () {
    var data = {};
    ['portfolio', 'location', 'phone', 'full_name', 'email'].forEach(function (key) {
      var el = this.field(key);
      if (el) data[key] = el.value;
    }, this);
    try { window.sessionStorage.setItem(this.storageKey, JSON.stringify(data)); } catch (e) { /* storage unavailable */ }
  };

  PphForm.prototype.restoreDraft = function () {
    var raw = null;
    try { raw = window.sessionStorage.getItem(this.storageKey); } catch (e) { return; }
    if (!raw) return;
    // Only restore when Shopify re-rendered the form with errors; otherwise discard.
    if (!this.form.querySelector('[data-pph-server-errors]')) {
      try { window.sessionStorage.removeItem(this.storageKey); } catch (e) { /* ignore */ }
      return;
    }
    var data;
    try { data = JSON.parse(raw); } catch (e) { return; }
    Object.keys(data || {}).forEach(function (key) {
      var el = this.field(key);
      if (el && !el.value && typeof data[key] === 'string') el.value = data[key];
    }, this);
  };

  PphForm.prototype.clearDraft = function () {
    try { window.sessionStorage.removeItem(this.storageKey); } catch (e) { /* ignore */ }
  };

  /* ---------- Submit ---------- */
  PphForm.prototype.onSubmit = function (e) {
    if (this.submitting) {
      e.preventDefault();
      return;
    }
    this.showFormError('');
    var firstInvalid = this.validateAll();
    if (firstInvalid) {
      e.preventDefault();
      this.showFormError(MSG.summary);
      firstInvalid.focus();
      return;
    }

    if (this.mode === 'sheet' && this.sheetUrl) {
      e.preventDefault();
      this.sendToSheet();
      return;
    }

    if (this.mode === 'endpoint' && this.endpoint) {
      e.preventDefault();
      this.sendToEndpoint();
      return;
    }

    // Shopify contact form: let the browser submit natively (supports Shopify's spam challenge).
    this.saveDraft();
    this.setLoading(true);
    this.announce(MSG.sending);
  };

  PphForm.prototype.sendToEndpoint = function () {
    var self = this;
    var data = new FormData();
    ['portfolio', 'full_name', 'location', 'email', 'phone', '_gotcha'].forEach(function (key) {
      var el = self.field(key);
      if (el) data.append(key, el.value.trim());
    });
    data.append('application', 'Potter Painter Hiring');
    data.append('page', window.location.href);
    if (this.fileInput && this.fileInput.files && this.fileInput.files[0]) {
      data.append('portfolio_file', this.fileInput.files[0], this.fileInput.files[0].name);
    }

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller ? window.setTimeout(function () { controller.abort(); }, 90000) : null;

    this.setLoading(true);
    this.announce(MSG.sending);

    fetch(this.endpoint, {
      method: 'POST',
      body: data,
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        return res
          .json()
          .catch(function () { return {}; })
          .then(function (body) { return { ok: res.ok, body: body }; });
      })
      .then(function (result) {
        if (timer) window.clearTimeout(timer);
        if (!result.ok || (result.body && result.body.ok === false)) {
          var message = MSG.server;
          var body = result.body || {};
          if (body.errors && body.errors.length && body.errors[0].message) {
            message = body.errors.map(function (er) { return er.message; }).join(' ');
          } else if (typeof body.error === 'string') {
            message = body.error;
          }
          throw new Error(message);
        }
        self.onSuccess();
      })
      .catch(function (err) {
        if (timer) window.clearTimeout(timer);
        var message = err && err.name !== 'TypeError' && err.name !== 'AbortError' && err.message ? err.message : MSG.network;
        self.setLoading(false);
        self.showFormError(message);
        self.announce(message);
      });
  };

  /* Google Apps Script web app: appends a row to the sheet and emails the notification address.
     Sent as application/x-www-form-urlencoded with no custom headers so it is a CORS "simple" request
     (no preflight, which Apps Script can't answer). Apps Script returns JSON with Access-Control-Allow-Origin: *. */
  PphForm.prototype.sendToSheet = function () {
    var self = this;
    var data = new URLSearchParams();
    ['portfolio', 'full_name', 'location', 'email', 'phone', '_gotcha'].forEach(function (key) {
      var el = self.field(key);
      if (el) data.append(key, el.value.trim());
    });
    data.append('application', 'Potter Painter Hiring');
    data.append('notify_email', this.notifyEmail);
    data.append('page', window.location.origin + window.location.pathname);

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller ? window.setTimeout(function () { controller.abort(); }, 45000) : null;

    this.setLoading(true);
    this.announce(MSG.sending);

    fetch(this.sheetUrl, {
      method: 'POST',
      body: data,
      redirect: 'follow',
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        return res
          .json()
          .catch(function () { return {}; })
          .then(function (body) { return { ok: res.ok, body: body }; });
      })
      .then(function (result) {
        if (timer) window.clearTimeout(timer);
        var body = result.body || {};
        if (!result.ok || body.ok !== true) {
          throw new Error(typeof body.error === 'string' && body.error ? body.error : MSG.server);
        }
        self.onSuccess();
      })
      .catch(function (err) {
        if (timer) window.clearTimeout(timer);
        var message = err && err.name !== 'TypeError' && err.name !== 'AbortError' && err.message ? err.message : MSG.network;
        self.setLoading(false);
        self.showFormError(message);
        self.announce(message);
      });
  };

  PphForm.prototype.onSuccess = function () {
    this.clearDraft();
    this.form.reset();
    this.form.hidden = true;
    if (this.success) {
      this.success.hidden = false;
      this.success.focus();
    }
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-pph]').forEach(function (root) {
      if (root.__pph) return;
      root.__pph = new PphForm(root);
      // Bring the confirmation into view after a Shopify contact-form redirect.
      var staticSuccess = root.querySelector('[data-pph-success-static]');
      if (staticSuccess && /contact_posted=true/.test(window.location.search)) {
        try { window.sessionStorage.removeItem(STORAGE_PREFIX + (root.querySelector('form') || {}).id); } catch (e) { /* ignore */ }
        staticSuccess.focus({ preventScroll: true });
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  // Theme Editor: re-initialise when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
